import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Edit2, BookOpen, FileText, TrendingUp, BarChart2,
  Eye, EyeOff, Users, MousePointerClick, Globe, Monitor,
  ExternalLink, Search, Share2, Zap,
} from 'lucide-react';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, Title, Tooltip, Legend, ArcElement, Filler,
} from 'chart.js';
import { fetchPosts, fetchPostStats, updatePost, Post, PostStats, CustomRange } from '../lib/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, ArcElement, Filler);

const RANGE_OPTIONS = [
  { key: '24h',    label: 'Daily'     },
  { key: '7d',     label: 'Weekly'    },
  { key: '1m',     label: 'Monthly'   },
  { key: '3m',     label: '3 Months'  },
  { key: '6m',     label: '6 Months'  },
  { key: '1y',     label: 'Yearly'    },
  { key: 'all',    label: 'All Time'  },
  { key: 'custom', label: '📅 Custom' },
];

function formatBucketLabel(ts: number, range: string): string {
  const d = new Date(ts * 1000);
  if (range === '24h') return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (['7d', '15d'].includes(range)) return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  if (['1m', '3m', '6m'].includes(range)) return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return d.toLocaleDateString([], { month: 'short', year: '2-digit' });
}

const PALETTE = [
  '#8B5CF6','#3B82F6','#10B981','#F59E0B','#EF4444',
  '#06B6D4','#84CC16','#F97316','#EC4899','#6366F1',
];

const SOURCE_ICONS: Record<string, string> = {
  Google: '🔍', Bing: '🔍', Yahoo: '🔍', DuckDuckGo: '🔍',
  Yandex: '🔍', Baidu: '🔍', Ecosia: '🔍', Brave: '🔍',
  Facebook: '📘', WhatsApp: '💬', Twitter: '🐦', Instagram: '📷',
  YouTube: '▶️', Reddit: '🤖', LinkedIn: '💼', TikTok: '🎵',
  Telegram: '✈️', Direct: '🔗',
};

const STATUS_BADGE: Record<string, string> = {
  published: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  draft:     'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  scheduled: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  disabled:  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const STATUS_LABEL: Record<string, string> = {
  published: 'Published', draft: 'Draft', scheduled: 'Scheduled', disabled: 'Unpublished',
};

const card = 'bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/60 dark:border-gray-700/60 p-5';

// Live blog URL per language — mirrors BLOG_HUB in
// frontend/web/functions/_shared/render-post.ts.
const SITE = 'https://inboxme.space';
const BLOG_HUB: Record<string, string> = {
  en: '/blog', tr: '/tr/blog', de: '/de/blog', fr: '/fr/blog', id: '/id/blog',
  pl: '/pl/blog', pt: '/pt/blog', ar: '/ar/blog', hi: '/hi/blog', ur: '/ur/blog', es: '/es/blog',
};
const inputCls = 'px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500';

// ── Small bar chart for breakdown list ─────────────────────────────────────
function BreakdownList({ title, items, icon }: { title: string; items: { label: string; count: number }[]; icon?: React.ReactNode }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? items : items.slice(0, 7);
  const max = items[0]?.count || 1;
  const total = items.reduce((s, i) => s + i.count, 0);
  return (
    <div className={card}>
      <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2 text-sm">
        {icon}{title}
      </h3>
      {items.length === 0 ? (
        <p className="text-sm text-gray-400 italic text-center py-6">No data for this period</p>
      ) : (
        <>
          <ul className="space-y-2">
            {shown.map((item, i) => {
              const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
              const icon = SOURCE_ICONS[item.label];
              return (
                <li key={item.label}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300 truncate max-w-[65%]">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: PALETTE[i % PALETTE.length] }} />
                      {icon && <span>{icon}</span>}
                      <span className="truncate">{item.label}</span>
                    </span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100 ml-2 whitespace-nowrap">
                      {item.count.toLocaleString()} <span className="text-gray-400 font-normal">({pct}%)</span>
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full transition-all" style={{ width: `${(item.count / max) * 100}%`, backgroundColor: PALETTE[i % PALETTE.length] }} />
                  </div>
                </li>
              );
            })}
          </ul>
          {items.length > 7 && (
            <button onClick={() => setExpanded(p => !p)} className="mt-3 text-xs text-purple-600 hover:text-purple-800 dark:text-purple-400 font-medium">
              {expanded ? '▲ Show less' : `▼ Show ${items.length - 7} more`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default function PostAnalytics({ baseRoute = '/blog' }: { baseRoute?: string } = {}) {
  const { id } = useParams<{ id: string }>();

  const [post, setPost]               = useState<Post | null>(null);
  const [range, setRange]             = useState('7d');
  const [custom, setCustom]           = useState<CustomRange | null>(null);
  const [customFrom, setCustomFrom]   = useState('');
  const [customTo, setCustomTo]       = useState('');
  const [stats, setStats]             = useState<PostStats | null>(null);
  const [postLoading, setPostLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statusBusy, setStatusBusy]   = useState(false);

  useEffect(() => {
    if (!id) return;
    fetchPosts().then(posts => {
      setPost(posts.find(x => x.id === id) ?? null);
      setPostLoading(false);
    });
  }, [id]);

  useEffect(() => {
    if (!post?.slug) return;
    if (range === 'custom' && !custom) return;
    setStatsLoading(true);
    fetchPostStats(post.slug, range, 14, range === 'custom' && custom ? custom : undefined)
      .then(s => { setStats(s); setStatsLoading(false); })
      .catch(() => setStatsLoading(false));
  }, [post?.slug, range, custom]);

  const applyCustom = () => {
    if (!customFrom || !customTo) return;
    setCustom({ from: Math.floor(new Date(customFrom).getTime() / 1000), to: Math.floor(new Date(customTo).getTime() / 1000) });
  };

  const changeStatus = async (newStatus: Post['status']) => {
    if (!post || !id) return;
    setStatusBusy(true);
    await updatePost(id, { status: newStatus });
    setPost(p => p ? { ...p, status: newStatus } : p);
    setStatusBusy(false);
  };

  const totalViews    = stats?.count ?? 0;
  const sessions      = stats?.sessions ?? 0;
  const organicClicks = stats?.organic_clicks ?? 0;
  const directTraffic = stats?.direct ?? 0;
  const peakBucket    = useMemo(() => Math.max(0, ...(stats?.series?.map(s => s.count) ?? [0])), [stats]);
  const ctr           = totalViews > 0 ? Math.round((organicClicks / totalViews) * 100) : 0;

  const ptype = post?.post_type || 'blog';

  const lineData = {
    labels: (stats?.series ?? []).map(p => formatBucketLabel(p.bucketStart, range)),
    datasets: [{
      label: 'Views',
      data: (stats?.series ?? []).map(p => p.count),
      fill: true,
      backgroundColor: 'rgba(139,92,246,0.12)',
      borderColor: 'rgba(139,92,246,1)',
      borderWidth: 2,
      pointRadius: 3,
      tension: 0.4,
    }],
  };

  const makeHBarData = (items: { label: string; count: number }[]) => ({
    labels: items.map(i => i.label),
    datasets: [{ data: items.map(i => i.count), backgroundColor: PALETTE, borderRadius: 4 }],
  });

  const makeDoughnutData = (items: { label: string; count: number }[]) => ({
    labels: items.map(i => i.label),
    datasets: [{ data: items.map(i => i.count), backgroundColor: PALETTE, borderWidth: 2 }],
  });

  const lineOpts = {
    responsive: true,
    plugins: { legend: { display: false } },
    scales: { x: { grid: { display: false } }, y: { beginAtZero: true, ticks: { precision: 0 } } },
  } as const;
  const barOpts = {
    responsive: true, indexAxis: 'y' as const,
    plugins: { legend: { display: false } },
    scales: { x: { beginAtZero: true, ticks: { precision: 0 } }, y: { grid: { display: false } } },
  } as const;
  const doughnutOpts = {
    responsive: true,
    plugins: { legend: { position: 'right' as const, labels: { boxWidth: 12, font: { size: 11 } } } },
  } as const;

  if (postLoading) return (
    <div className="flex items-center justify-center py-24">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
    </div>
  );
  if (!post) return (
    <div className="text-center py-24 text-gray-400 space-y-3">
      <p className="text-lg">Post not found.</p>
      <Link to={baseRoute} className="inline-flex items-center gap-1 text-blue-500 hover:text-blue-600 text-sm"><ArrowLeft size={14} /> Back</Link>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-1 min-w-0">
          <Link to={baseRoute} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            <ArrowLeft size={14} /> {baseRoute === '/guides' ? 'Guides' : 'Blog & Guides'}
          </Link>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
              ptype === 'guide' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
            }`}>
              {ptype === 'guide' ? <BookOpen size={11} /> : <FileText size={11} />} {ptype}
            </span>
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_BADGE[post.status] ?? ''}`}>
              {STATUS_LABEL[post.status] || post.status}
            </span>
            <span className="text-xs font-mono text-gray-400 truncate">{post.slug}</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 leading-snug">{post.title}</h1>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
          <Link to={`${baseRoute}/edit/${post.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl text-sm text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition">
            <Edit2 size={14} /> Edit
          </Link>
          {post.status === 'published' ? (
            <button onClick={() => changeStatus('draft')} disabled={statusBusy}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl text-sm text-amber-700 dark:text-amber-300 hover:bg-amber-100 transition disabled:opacity-50">
              <EyeOff size={14} /> Unpublish
            </button>
          ) : (
            <button onClick={() => changeStatus('published')} disabled={statusBusy}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-xl text-sm text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 transition disabled:opacity-50">
              <Eye size={14} /> Publish
            </button>
          )}
        </div>
      </div>

      {/* Per-post notice — makes it 100% clear this data belongs to THIS post only */}
      <div className="flex items-start gap-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700/50 rounded-2xl px-5 py-4">
        <BarChart2 size={18} className="text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-purple-900 dark:text-purple-200">
            Analytics for this {ptype === 'guide' ? 'guide' : 'blog'} only
          </p>
          <p className="text-xs text-purple-700 dark:text-purple-300 mt-0.5 leading-relaxed">
            Only visitors for <span className="font-bold">"{post.title}"</span> are shown here.
            Data from other blogs or guides will not appear.
          </p>
        </div>
      </div>

      {/* SEO & indexing — what search engines actually see for this URL */}
      <div className={card}>
        <div className="flex items-center gap-2 mb-3">
          <Search size={16} className="text-emerald-600 dark:text-emerald-400" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">SEO &amp; Indexing</h3>
          <span className={`ml-auto px-2.5 py-1 rounded-full text-[10px] font-semibold ${STATUS_BADGE[post.status] || STATUS_BADGE.draft}`}>
            {STATUS_LABEL[post.status] || post.status}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div>
            <p className="text-gray-500 dark:text-gray-400">Pipeline</p>
            <p className="font-semibold text-gray-900 dark:text-gray-100">
              {post.is_dynamic || ptype !== 'guide' ? 'Dynamic (D1 → SSR)' : 'Prerendered'}
            </p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400">Published</p>
            <p className="font-semibold text-gray-900 dark:text-gray-100">
              {post.publish_at ? new Date(post.publish_at * 1000).toLocaleDateString() : '—'}
            </p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400">Updated</p>
            <p className="font-semibold text-gray-900 dark:text-gray-100">
              {post.updated_at ? new Date(post.updated_at * 1000).toLocaleDateString() : '—'}
            </p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400">IndexNow ping</p>
            <p className={`font-semibold ${post.indexnotified_at ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`}>
              {post.indexnotified_at ? new Date(post.indexnotified_at * 1000).toLocaleDateString() : 'never'}
            </p>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-gray-200/60 dark:border-gray-700/60 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
          <a
            href={`${SITE}${BLOG_HUB[post.lang] ?? BLOG_HUB.en}/${post.slug}/`}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
          >
            <ExternalLink size={12} /> {SITE}{BLOG_HUB[post.lang] ?? BLOG_HUB.en}/{post.slug}/
          </a>
          <span className={post.status === 'published' ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}>
            {post.status === 'published' ? '✓ in sitemap + RSS' : 'not in sitemap (not published)'}
          </span>
          <a
            href={`https://search.google.com/test/rich-results?url=${encodeURIComponent(`${SITE}${BLOG_HUB[post.lang] ?? BLOG_HUB.en}/${post.slug}/`)}`}
            target="_blank" rel="noreferrer" className="text-purple-600 dark:text-purple-400 hover:underline"
          >
            Rich results test ↗
          </a>
        </div>
        <p className="mt-2 text-[10px] text-gray-400">
          Server-side: title, description, canonical, hreflang + x-default, OG/Twitter, article timestamps,
          BlogPosting + BreadcrumbList + FAQPage JSON-LD, cover image and the rendered article body.
        </p>
      </div>

      {/* Range tabs */}
      <div className="flex flex-wrap gap-1 bg-gray-100 dark:bg-gray-800/60 p-1 rounded-xl w-fit">
        {RANGE_OPTIONS.map(r => (
          <button key={r.key} onClick={() => { setRange(r.key); if (r.key !== 'custom') setCustom(null); }}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${range === r.key ? 'bg-white dark:bg-gray-700 shadow text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}>
            {r.label}
          </button>
        ))}
      </div>
      {range === 'custom' && (
        <div className="flex items-center gap-3 flex-wrap">
          <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className={inputCls} />
          <span className="text-gray-400">→</span>
          <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className={inputCls} />
          <button onClick={applyCustom} disabled={!customFrom || !customTo}
            className="px-4 py-2 bg-purple-600 text-white rounded-xl text-sm hover:bg-purple-700 transition disabled:opacity-50">Apply</button>
        </div>
      )}

      {/* ── KPI stat cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Total Views',     value: totalViews,    icon: Eye,              color: 'text-purple-500', bg: 'from-purple-50 to-purple-100/40 dark:from-purple-900/20 dark:to-purple-900/10' },
          { label: 'Sessions',        value: sessions,       icon: Users,            color: 'text-blue-500',   bg: 'from-blue-50 to-blue-100/40 dark:from-blue-900/20 dark:to-blue-900/10' },
          { label: 'Impressions',     value: totalViews,    icon: BarChart2,        color: 'text-indigo-500', bg: 'from-indigo-50 to-indigo-100/40 dark:from-indigo-900/20 dark:to-indigo-900/10' },
          { label: 'Organic Clicks',  value: organicClicks,  icon: MousePointerClick,color: 'text-emerald-500',bg: 'from-emerald-50 to-emerald-100/40 dark:from-emerald-900/20 dark:to-emerald-900/10' },
          { label: 'Direct Traffic',  value: directTraffic,  icon: Zap,              color: 'text-amber-500',  bg: 'from-amber-50 to-amber-100/40 dark:from-amber-900/20 dark:to-amber-900/10' },
          { label: 'Peak Views',      value: peakBucket,    icon: TrendingUp,       color: 'text-rose-500',   bg: 'from-rose-50 to-rose-100/40 dark:from-rose-900/20 dark:to-rose-900/10' },
        ].map(s => (
          <div key={s.label} className={`bg-gradient-to-br ${s.bg} rounded-2xl border border-gray-200/60 dark:border-gray-700/60 p-4`}>
            <div className="flex items-center gap-1.5 mb-1.5">
              <s.icon size={13} className={s.color} />
              <span className="text-[10px] text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">{s.label}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {statsLoading ? <span className="text-gray-300 dark:text-gray-600 animate-pulse">—</span> : s.value.toLocaleString()}
            </p>
          </div>
        ))}
      </div>

      {/* CTR banner */}
      {!statsLoading && totalViews > 0 && (
        <div className="flex items-center gap-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-2xl px-5 py-3">
          <MousePointerClick size={18} className="text-purple-600 dark:text-purple-400 flex-shrink-0" />
          <div className="flex-1 text-sm text-purple-700 dark:text-purple-300">
            <span className="font-bold">{ctr}%</span> organic CTR — {organicClicks.toLocaleString()} clicks from {totalViews.toLocaleString()} views came from search engines
          </div>
        </div>
      )}

      {/* Views over time */}
      <div className={card}>
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          📈 Views Over Time
          {statsLoading && <span className="text-xs text-gray-400 animate-pulse font-normal">Loading…</span>}
        </h3>
        {statsLoading
          ? <div className="h-52 flex items-center justify-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-400" /></div>
          : totalViews === 0
            ? <div className="h-52 flex items-center justify-center text-gray-400 text-sm">No views recorded for this period.</div>
            : <Line data={lineData} options={lineOpts} />}
      </div>

      {/* Traffic Sources — where users came from */}
      <div className={card}>
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <ExternalLink size={16} className="text-blue-500" /> Traffic Sources — Where Users Came From
        </h3>
        {statsLoading ? (
          <div className="h-32 flex items-center justify-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400" /></div>
        ) : (stats?.breakdown?.sources?.length ?? 0) === 0 ? (
          <p className="text-gray-400 text-sm text-center py-8">No referrer data for this period</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <ul className="space-y-2">
                {(stats?.breakdown?.sources ?? []).map((src, i) => {
                  const total = (stats?.breakdown?.sources ?? []).reduce((s, x) => s + x.count, 0);
                  const pct = total > 0 ? Math.round((src.count / total) * 100) : 0;
                  const icon = SOURCE_ICONS[src.label] || '🌐';
                  return (
                    <li key={src.label}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300 truncate">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: PALETTE[i % PALETTE.length] }} />
                          <span>{icon}</span>
                          <span className="truncate font-medium">{src.label}</span>
                        </span>
                        <span className="font-bold text-gray-900 dark:text-gray-100 whitespace-nowrap ml-2">
                          {src.count.toLocaleString()} <span className="text-gray-400 font-normal">({pct}%)</span>
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                        <div className="h-2 rounded-full" style={{ width: `${pct}%`, backgroundColor: PALETTE[i % PALETTE.length] }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="flex items-center justify-center">
              {(stats?.breakdown?.sources?.length ?? 0) > 0 && (
                <Doughnut data={makeDoughnutData(stats!.breakdown.sources.slice(0, 8))} options={doughnutOpts} />
              )}
            </div>
          </div>
        )}
      </div>

      {/* 2-col breakdown grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* Referrer domains */}
        <BreakdownList
          title="Top Referrer Domains"
          icon={<ExternalLink size={14} className="text-blue-500" />}
          items={stats?.breakdown?.referrers ?? []}
        />

        {/* Search engines */}
        <BreakdownList
          title="Organic Search Traffic"
          icon={<Search size={14} className="text-emerald-500" />}
          items={stats?.breakdown?.engines ?? []}
        />

        {/* Devices */}
        <div className={card}>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2 text-sm">
            <Monitor size={14} className="text-purple-500" /> Devices
          </h3>
          {statsLoading
            ? <div className="h-40 flex items-center justify-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-400" /></div>
            : (stats?.breakdown?.devices?.length ?? 0) > 0
              ? (
                <div className="grid grid-cols-2 gap-4 items-center">
                  <Doughnut data={makeDoughnutData(stats!.breakdown.devices)} options={doughnutOpts} />
                  <ul className="space-y-2">
                    {stats!.breakdown.devices.map((d, i) => {
                      const total = stats!.breakdown.devices.reduce((s, x) => s + x.count, 0);
                      return (
                        <li key={d.label} className="flex items-center justify-between text-xs">
                          <span className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: PALETTE[i % PALETTE.length] }} />
                            {d.label}
                          </span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">
                            {Math.round((d.count / total) * 100)}%
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )
              : <p className="text-gray-400 text-sm text-center py-10">No data for this period</p>}
        </div>

        {/* Browsers */}
        <div className={card}>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2 text-sm">
            <Globe size={14} className="text-indigo-500" /> Browsers
          </h3>
          {statsLoading
            ? <div className="h-40 flex items-center justify-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-400" /></div>
            : (stats?.breakdown?.browsers?.length ?? 0) > 0
              ? <Bar data={makeHBarData(stats!.breakdown.browsers)} options={barOpts} />
              : <p className="text-gray-400 text-sm text-center py-10">No data for this period</p>}
        </div>

      </div>
    </div>
  );
}
